<script lang="ts">
  import { fade } from 'svelte/transition';
  import { browser } from '$app/environment';
  import type { AspectRatio } from '../../../types/images';
  import type { AnimationConfig } from '../../animations/types';
  import AnimatedComponent from '../../animations/AnimatedComponent.svelte';

  export let src: string | undefined = undefined;
  export let alt: string | undefined = undefined;
  export let aspectRatio: AspectRatio | undefined = undefined;
  export let svg: string | undefined = undefined;
  export let scale: number = 1;
  export let animation: AnimationConfig | undefined = undefined;
  export let backgroundSize: 'cover' | 'contain' = 'cover';
  export let sources: Array<{ media: string; srcset: string }> | undefined = undefined;
  export let sizes: string | undefined = undefined;
  export let originalSrc: string | undefined = undefined; // Original URL for fallback

  let currentSrc: string | undefined;
  let fallbackIndex = 0;
  let imgElement: HTMLImageElement;
  let bgElement: HTMLDivElement;

  $: currentSrc = src;
  $: computedAspectRatio = aspectRatio?.replace(':', ' / ') || '4:3';
  $: hasSvg = !!svg;
  $: hasImage = !!src;
  $: hasAnimation = !!animation;
  $: hasResponsiveImage = !!(src && sources?.length);
  $: transform = `scale(${scale})`;
  $: bgSize = backgroundSize === 'cover' ? 'background-size: cover;' : 'background-size: contain;';

  // Build fallback chain from sources and main URL
  function getFallbackChain(): string[] {
    const chain: string[] = [];

    // Add all source URLs first (in reverse order - largest to smallest)
    if (sources && sources.length > 0) {
      const sourceUrls = sources.map(s => s.srcset).reverse();
      chain.push(...sourceUrls);
    }

    // Add the main URL if not already in chain
    if (src && !chain.includes(src)) {
      chain.push(src);
    }

    // Add original URL as final fallback if available
    if (originalSrc && !chain.includes(originalSrc)) {
      chain.push(originalSrc);
    }

    return chain;
  }

  function handleImageError() {
    const fallbackChain = getFallbackChain();
    fallbackIndex++;

    if (fallbackIndex < fallbackChain.length) {
      console.warn(`Image failed to load: ${currentSrc}, trying fallback ${fallbackIndex}/${fallbackChain.length}`);
      currentSrc = fallbackChain[fallbackIndex];

      // Update the appropriate element
      if (imgElement) {
        imgElement.src = currentSrc;
      } else if (bgElement) {
        bgElement.style.backgroundImage = `url('${currentSrc}')`;
      }
    } else {
      console.error(`All image fallbacks failed for: ${src}`, fallbackChain);
    }
  }

  // Handle background image error (using Image object)
  function setupBackgroundFallback(url: string) {
    if (!browser) return;
    const img = new Image();
    img.onerror = handleImageError;
    img.src = url;
  }

  // Reset fallback when src changes
  $: if (src) {
    fallbackIndex = 0;
    currentSrc = src;
  }

  // Setup background image error handling
  $: if (hasImage && !hasResponsiveImage && currentSrc) {
    setupBackgroundFallback(currentSrc);
  }
</script>

<div class="image-wrapper" class:has-animation={hasAnimation} style="--aspect-ratio: {computedAspectRatio}; --transform: {transform}">
  <div class="content" transition:fade={{ duration: 500 }}>
    {#if hasAnimation && animation}
      <AnimatedComponent config={animation} {scale} />
    {:else if hasSvg}
      <div class="svg-container">
        {@html svg}
      </div>
    {:else if hasResponsiveImage}
      <div class="image-container">
        <picture>
          {#each sources as source}
            <source media={source.media} srcset={source.srcset} />
          {/each}
          <img
            bind:this={imgElement}
            src={currentSrc}
            {alt}
            {sizes}
            class="responsive-image"
            style="object-fit: {backgroundSize};"
            loading="lazy"
            on:error={handleImageError}
          />
        </picture>
      </div>
    {:else if hasImage}
      <div class="image-container">
        <div
          bind:this={bgElement}
          class="image"
          style="background-image: url('{currentSrc}'); {bgSize}"
          role="img"
          aria-label={alt}
          data-src={currentSrc}
        ></div>
      </div>
    {/if}
  </div>
</div>

<style>
  .image-wrapper {
    width: 100%;
    height: 100%;
    aspect-ratio: var(--aspect-ratio);
    overflow: hidden;
    transform: var(--transform);
    transform-origin: center bottom;
  }

  .image-wrapper.has-animation {
    aspect-ratio: unset;
  }

  .content {
    width: 100%;
    height: 100%;
  }

  .image-container {
    width: 100%;
    height: 100%;
    position: relative;
  }

  .svg-container {
    width: 100%;
    height: 100%;
    display: flex;
    align-items: center;
    justify-content: center;
  }

  .svg-container :global(svg) {
    width: 100%;
    height: 100%;
    object-fit: contain;
  }

  .image {
    width: 100%;
    height: 100%;
    background-size: cover;
    background-position: center;
    background-repeat: no-repeat;
    opacity: 1;
    position: absolute;
    inset: 0;
    z-index: 2;
  }

  .responsive-image {
    width: 100%;
    height: 100%;
    position: absolute;
    inset: 0;
    z-index: 2;
    object-position: center;
  }

  .shimmer-container {
    position: absolute;
    inset: 0;
    z-index: 1;
    overflow: hidden;
  }

  .shimmer {
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    background: linear-gradient(
      90deg,
      transparent 0%,
      rgba(255, 255, 255, 0.4) 20%,
      rgba(255, 255, 255, 0.6) 50%,
      rgba(255, 255, 255, 0.4) 80%,
      transparent 100%
    );
    background-size: 200% 100%;
    animation: shimmer 1.5s ease-in-out infinite;
    will-change: background-position;
  }

  @keyframes shimmer {
    from {
      background-position: -200% 0;
    }
    to {
      background-position: 200% 0;
    }
  }

  /* Make sure shimmer is visible on dark themes */
  :global([data-theme='dark']) .shimmer {
    background: linear-gradient(
      90deg,
      transparent 0%,
      rgba(255, 255, 255, 0.15) 20%,
      rgba(255, 255, 255, 0.3) 50%,
      rgba(255, 255, 255, 0.15) 80%,
      transparent 100%
    );
  }
</style>
